import * as XLSX from 'xlsx';
import { DeclineCurveAnalysisTableResponse } from 'types/api-responses';
import { DiFormat, QiFormat } from 'types/api-payloads';

export interface DcaFormValues {
  diFormat: DiFormat;
  qiFormat: QiFormat;
  forecastDuration: number | null;
  forecastDurationOption: string;
  oil: {
    dlim: number | null;
    bFactor: number | null;
    declineModel: string;
    qi: number | null;
    di: number | null;
    startDate: Date | null;
    rampupModel: string | null;
    qiRampup: number | null;
    rampupDuration: number | null;
    n: number | null;
    ri: number | null;
    tElf: number | null;
    m: number | null;
  };
  gas: {
    dlim: number | null;
    bFactor: number | null;
    declineModel: string;
    qi: number | null;
    di: number | null;
    startDate: Date | null;
    rampupModel: string | null;
    qiRampup: number | null;
    rampupDuration: number | null;
    n: number | null;
    ri: number | null;
    tElf: number | null;
    m: number | null;
  };
  water: {
    dlim: number | null;
    bFactor: number | null;
    declineModel: string;
    qi: number | null;
    di: number | null;
    startDate: Date | null;
    rampupModel: string | null;
    qiRampup: number | null;
    rampupDuration: number | null;
    n: number | null;
    ri: number | null;
    tElf: number | null;
    m: number | null;
  };
}

interface ExportDcaToXlsxParams {
  uwi: string;
  wellName?: string;
  tableData: DeclineCurveAnalysisTableResponse | null;
  formValues: DcaFormValues;
}

/**
 * Formats a nullable number by either returning it, or returning an empty string if the input is null.
 * @param {number | null} value
 * @return {number | string}
 */
const formatNumber = (value: number | null): number | string => {
  return value !== null && value !== undefined ? value : '';
};

/**
 * Converts a date string or Date object to a local date without time components. This avoids timezone-related date changes when exporting to Excel.
 * @param {string | Date | null} date Date string (e.g., "2024-01-15") or Date object to normalize.
 * @return {Date | null}
 */
const toLocalDate = (date: string | Date | null): Date | null => {
  if (!date) return null;

  if (typeof date === 'string') {
    const [year, month, day] = date.split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
};

/**
 * Creates the "DCA Forecast Volumes" sheet data.
 * @param {string} uwi
 * @param {string} wellName
 * @param {Array} dcaRows
 * @return {Array}
 */
const createVolumesSheetData = (
  uwi: string,
  wellName: string,
  dcaRows: DeclineCurveAnalysisTableResponse['dcaTable'][0]['dcaRows']
): Array<any> => {
  // Same column names the DCA table uses, so a reader can line the two up.
  const headers = [
    'UWI',
    'Well Name',
    'Date',
    'Months Online',
    'Oil Production Volumes (bbl)',
    'Gas Production Volumes (mcf)',
    'Water Production Volumes (bbl)',
    'Oil Model Forecast (bbl)',
    'Gas Model Forecast (mcf)',
    'Water Model Forecast (bbl)',
    'Gross Oil (bbl)',
    'Gross Gas (mcf)',
    'Gross Water (bbl)',
  ];

  const dataRows = dcaRows.map((row) => [
    uwi,
    wellName,
    toLocalDate(row.productionDate),
    formatNumber(row.monthsOnline),
    formatNumber(row.oilProductionVolume),
    formatNumber(row.gasProductionVolume),
    formatNumber(row.waterProductionVolume),
    formatNumber(row.oilModelForecast),
    formatNumber(row.gasModelForecast),
    formatNumber(row.waterModelForecast),
    formatNumber(row.grossOil),
    formatNumber(row.grossGas),
    formatNumber(row.grossWater),
  ]);

  return [headers, ...dataRows];
};

/**
 * Create stream-specific parameter rows based on model types and parameters.
 * @param {string} streamName
 * @param {Object} streamValues Stream-specific form values.
 * @return {Array}
 */
const createStreamParameterRows = (
  streamName: 'Oil' | 'Gas' | 'Water',
  streamValues:
    DcaFormValues['oil'] | DcaFormValues['gas'] | DcaFormValues['water']
): any[][] => {
  const rows: any[][] = [];
  const isRatio = streamValues.declineModel === 'ratio';
  const isModifiedHyperbolic =
    streamValues.declineModel === 'modified_hyperbolic';
  const isHyperbolic =
    streamValues.declineModel === 'hyperbolic' || isModifiedHyperbolic;

  rows.push([
    `${streamName} Forecast Start Date`,
    toLocalDate(streamValues.startDate),
  ]);
  rows.push([`${streamName} DCA Model`, streamValues.declineModel]);

  if (isRatio) {
    rows.push([`${streamName} Ri`, formatNumber(streamValues.ri)]);
    rows.push([`${streamName} t_elf months`, formatNumber(streamValues.tElf)]);
    rows.push([`${streamName} m`, formatNumber(streamValues.m)]);
  } else {
    rows.push([`${streamName} qi`, formatNumber(streamValues.qi)]);
    rows.push([`${streamName} Di`, formatNumber(streamValues.di)]);

    if (isHyperbolic) {
      rows.push([`${streamName} b-factor`, formatNumber(streamValues.bFactor)]);
    }

    if (isModifiedHyperbolic) {
      rows.push([`${streamName} Dlim`, formatNumber(streamValues.dlim)]);
    }

    // Ramp-up fields, only if NOT ratio AND ramp-up model is NOT 'none'
    const hasRampup =
      streamValues.rampupModel && streamValues.rampupModel !== 'none';

    rows.push([
      `${streamName} Ramp Up Model`,
      streamValues.rampupModel || 'none',
    ]);

    if (hasRampup) {
      rows.push([
        `${streamName} Ramp Up qi`,
        formatNumber(streamValues.qiRampup),
      ]);
      rows.push([
        `${streamName} Ramp Up Duration Months`,
        formatNumber(streamValues.rampupDuration),
      ]);

      if (
        streamValues.rampupModel === 'linear' ||
        streamValues.rampupModel === 'exponential'
      ) {
        rows.push([`${streamName} Ramp Up n`, formatNumber(streamValues.n)]);
      }
    }
  }

  return rows;
};

/**
 * Creates the "DCA Forecast Parameters" sheet data.
 * @param {string} uwi The UWI
 * @param {Object} formValues The DCA form values
 * @return {Array} Array containing parameter labels and values
 */
const createParametersSheetData = (
  uwi: string,
  formValues: DcaFormValues
): any[][] => {
  const rows: any[][] = [
    ['UWI', uwi],
    ['Qi Format', formValues.qiFormat],
    ['Di Format', formValues.diFormat],
    [
      'Forecast Duration Format',
      formValues.forecastDurationOption === 'forecast_start'
        ? 'From Forecast Start'
        : formValues.forecastDurationOption === 'first_production'
          ? 'From First Production'
          : formValues.forecastDurationOption,
    ],
    ['Forecast Duration Period', formatNumber(formValues.forecastDuration)],
  ];

  rows.push(...createStreamParameterRows('Oil', formValues.oil));
  rows.push(...createStreamParameterRows('Gas', formValues.gas));
  rows.push(...createStreamParameterRows('Water', formValues.water));

  return rows;
};

/**
 * Generates and opens a prompt to save the resulting `xlsx` file.
 */
export const exportDcaToXlsx = ({
  uwi,
  wellName = '',
  tableData,
  formValues,
}: ExportDcaToXlsxParams): void => {
  if (!tableData || !tableData.dcaTable || tableData.dcaTable.length === 0) {
    console.error('No table data available for export');
    return;
  }

  const dcaRows = tableData.dcaTable[0].dcaRows;
  if (!dcaRows || dcaRows.length === 0) {
    console.error('No DCA rows available for export');
    return;
  }

  const workbook = XLSX.utils.book_new();

  const volumesData = createVolumesSheetData(uwi, wellName, dcaRows);
  const volumesSheet = XLSX.utils.aoa_to_sheet(volumesData);

  const range = XLSX.utils.decode_range(volumesSheet['!ref'] || 'A1');
  for (let row = range.s.r + 1; row <= range.e.r; row++) {
    const cellAddress = XLSX.utils.encode_cell({ r: row, c: 2 });
    if (volumesSheet[cellAddress]) {
      volumesSheet[cellAddress].z = 'yyyy-mm-dd';
    }
  }

  // Set column widths of volumes sheet
  volumesSheet['!cols'] = [
    // TODO: magic numbers
    { wch: 11.5 }, // UWI
    { wch: 25 }, // Well Name
    { wch: 12 }, // Date
    { wch: 14 }, // Months Online
    { wch: 15 }, // Oil Production Volumes
    { wch: 15 }, // Gas Production Volumes
    { wch: 15 }, // Water Production Volumes
    { wch: 15 }, // Oil Model Forecast
    { wch: 15 }, // Gas Model Forecast
    { wch: 15 }, // Water Model Forecast
    { wch: 15 }, // Gross Oil
    { wch: 15 }, // Gross Gas
    { wch: 15 }, // Gross Water
  ];

  XLSX.utils.book_append_sheet(workbook, volumesSheet, 'DCA Forecast Volumes');

  const parametersData = createParametersSheetData(uwi, formValues);
  const parametersSheet = XLSX.utils.aoa_to_sheet(parametersData);

  const paramRange = XLSX.utils.decode_range(parametersSheet['!ref'] || 'A1');
  for (let row = paramRange.s.r; row <= paramRange.e.r; row++) {
    const labelCell = parametersSheet[XLSX.utils.encode_cell({ r: row, c: 0 })];
    if (
      labelCell &&
      labelCell.v &&
      String(labelCell.v).includes('Forecast Start Date')
    ) {
      const dateCell = XLSX.utils.encode_cell({ r: row, c: 1 });
      if (parametersSheet[dateCell]) {
        parametersSheet[dateCell].z = 'yyyy-mm-dd';
      }
    }
  }

  // Set column widths of parameters sheet
  parametersSheet['!cols'] = [
    // TODO: magic numbers
    { wch: 22.5 }, // Parameter name
    { wch: 18 }, // Value
  ];

  XLSX.utils.book_append_sheet(
    workbook,
    parametersSheet,
    'DCA Forecast Parameters'
  );

  const now = new Date();
  const date = now.toISOString().split('T')[0].replace(/-/g, '.');
  const time = now.toTimeString().split(' ')[0].replace(/:/g, '.');
  const timestamp = `${date}_${time}`;
  const sanitizedWellName = wellName.replace(/[^a-zA-Z0-9]/g, '_');
  const filename = `DCA_Export_${sanitizedWellName || uwi}_${timestamp}.xlsx`;

  XLSX.writeFile(workbook, filename);
};
